import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { Layout } from "./components/Layout";
import { ErrorToast } from "./components/ui/ErrorToast";
import PageLoader from "./components/ui/PageLoader";
import "./i18n";

const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const RequestsListPage = lazy(() => import("./pages/request/RequestsListPage"));
const NewRequestPage = lazy(() => import("./pages/request/NewRequestPage"));
const ViewRequestPage = lazy(() => import("./pages/request/ViewRequestPage"));
const EditRequestPage = lazy(() => import("./pages/request/EditRequestPage"));
const InventoryListPage = lazy(() => import("./pages/inventory/InventoryListPage"));
const NewInventoryPage = lazy(() => import("./pages/inventory/NewInventoryPage"));
const ViewInventoryPage = lazy(() => import("./pages/inventory/ViewInventoryPage"));
const EditInventoryPage = lazy(() => import("./pages/inventory/EditInventoryPage"));
const ProfilePage = lazy(() => import("./pages/users/ProfilePage"));
const AdminPage = lazy(() => import("./pages/admin/AdminPage"));
const UsersListPage = lazy(() => import("./pages/users/UsersListPage"));
const NotificationsListPage = lazy(() => import("./pages/notifications/NotificationsListPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route
                path="/"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </Suspense>
                }
              />
              <Route
                path="/requests"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <RequestsListPage />
                  </Suspense>
                }
              />
              <Route
                path="/requests/new"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <NewRequestPage />
                  </Suspense>
                }
              />
              <Route
                path="/requests/:id"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ViewRequestPage />
                  </Suspense>
                }
              />
              <Route
                path="/requests/:id/edit"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EditRequestPage />
                  </Suspense>
                }
              />
              <Route
                path="/inventory"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <InventoryListPage />
                  </Suspense>
                }
              />
              <Route
                path="/inventory/new"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <NewInventoryPage />
                  </Suspense>
                }
              />
              <Route
                path="/inventory/:id"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ViewInventoryPage />
                  </Suspense>
                }
              />
              <Route
                path="/inventory/:id/edit"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EditInventoryPage />
                  </Suspense>
                }
              />
              <Route
                path="/users"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <UsersListPage />
                  </Suspense>
                }
              />
              <Route
                path="/profile"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminPage />
                  </Suspense>
                }
              />
              <Route
                path="/notifications"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <NotificationsListPage />
                  </Suspense>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <ErrorToast />
    </QueryClientProvider>
  );
}
